import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import {
  MEDIA_SUBJECT_GROUP_CLASS,
  MEDIA_SUBJECT_LIST_CLASS,
  MEDIA_SUBJECT_LIST_MAX_HEIGHT,
  MEDIA_SUBJECT_RADIO_CLASS,
  MEDIA_SUBJECT_ROW_CLASS,
  SEARCH_THRESHOLD,
} from 'modules/championship/ui/components/form-parts/media/media-subject-picker-metrics';
import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { theme } from 'styles/theme/theme';
import UDSearchInput from 'ud-ui/components/search';
import UDText from 'ud-ui/components/text';

/** Объект генерации, который можно выбрать в окне «Сгенерировать». */
export interface MediaSubjectItem {
  id: number;
  title: string;
  /** Вторая строка: дата матча и т. п. */
  meta?: string;
  /** Заголовок группы, под которым показывается пункт (тур для матча). */
  group?: string;
  /** Почему пункт нельзя выбрать; задан — пункт виден, но недоступен. */
  disabledReason?: string;
}

interface MediaSubjectPickerProps {
  items: MediaSubjectItem[];
  value: number | null;
  onChange: (id: number) => void;
  /** Подпись группы радиокнопок для экранных читалок. */
  label: string;
  /** Подсказка в поле поиска — по умолчанию про матчи окна «Сгенерировать». */
  searchPlaceholder?: string;
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е');
}

/**
 * Список выбора одного объекта прямо в теле окна — вместо выпадающего списка.
 *
 * Окно «Сгенерировать» существует ради одного выбора, а выпадашка внутри диалога либо
 * обрезается им, либо перекрывает его кнопки (IMGM-1508). Поэтому список виден сразу, у него
 * своя высота и скролл, а заголовок и кнопки окна остаются на месте. Строки — нативные
 * радиокнопки: стрелки, Tab и экранные читалки работают без собственной клавиатурной логики,
 * а Enter на выбранной радиокнопке отправляет форму окна (неявная отправка формы HTML) —
 * поэтому компонент должен стоять внутри `<form>`.
 */
const MediaSubjectPicker = ({ items, value, onChange, label, searchPlaceholder }: MediaSubjectPickerProps) => {
  const { t } = useLingui();
  const name = useId();
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);

  // Высота списка замеряется при первом показе и дальше не меняется (IMGM-1508): иначе на каждый
  // символ поиска окно, центрированное по вертикали, меняло высоту, и поле поиска уезжало из-под
  // курсора. Длинный список упирается в предел, короткий так и остаётся по своему размеру.
  // `useLayoutEffect` — до отрисовки, так что неподогнанного кадра не видно.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (lockedHeight !== null || !list) return;
    const borders = list.offsetHeight - list.clientHeight;
    setLockedHeight(Math.min(list.scrollHeight + borders, MEDIA_SUBJECT_LIST_MAX_HEIGHT));
  }, [lockedHeight]);

  const visible = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return items;
    return items.filter((item) => [item.title, item.meta, item.group].some((part) => part && normalize(part).includes(needle)));
  }, [items, query]);

  // Группы в порядке первого появления: порядок туров приходит с бэкенда и уже правильный.
  const groups = useMemo(() => {
    const result: { title?: string; items: MediaSubjectItem[] }[] = [];
    visible.forEach((item) => {
      const last = result[result.length - 1];
      if (last && last.title === item.group) last.items.push(item);
      else result.push({ title: item.group, items: [item] });
    });
    return result;
  }, [visible]);

  // Enter в поиске уточняет фильтр, а не запускает генерацию ранее выбранного пункта:
  // гасим неявную отправку формы только для поля поиска.
  const preventSearchSubmit = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  return (
    <div className="flex flex-col gap-3">
      {items.length >= SEARCH_THRESHOLD && (
        <UDSearchInput
          value={query}
          // Видимая подпись: плейсхолдер исчезает при вводе и подписью поля не считается.
          label={t`Поиск`}
          iconPosition="icon-left"
          placeholder={searchPlaceholder ?? t`Название команды, тура или дата`}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={preventSearchSubmit}
          data-testid="media-subject-search"
        />
      )}

      <div
        ref={listRef}
        role="radiogroup"
        aria-label={label}
        className={MEDIA_SUBJECT_LIST_CLASS}
        // До замера — только предел; после — ровно замеренная высота, при поиске строки прижаты кверху.
        style={lockedHeight === null ? { maxHeight: MEDIA_SUBJECT_LIST_MAX_HEIGHT } : { height: lockedHeight }}
        data-testid="media-subject-list"
      >
        {visible.length === 0 ? (
          <UDText type="subhead" className="text-inkSecondary block p-4">
            <Trans>Ничего не найдено</Trans>
          </UDText>
        ) : (
          groups.map((group, index) => (
            <div key={group.title ?? `group-${index}`}>
              {group.title && (
                <UDText type="footnote" className={MEDIA_SUBJECT_GROUP_CLASS}>
                  {group.title}
                </UDText>
              )}
              {group.items.map((item) => {
                const isDisabled = Boolean(item.disabledReason);
                const isSelected = item.id === value;
                return (
                  <label
                    key={item.id}
                    className={classNames(
                      MEDIA_SUBJECT_ROW_CLASS,
                      isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      isSelected ? 'bg-CasePendingSurfaceActive' : !isDisabled && 'hover:bg-accentCornsilk',
                    )}
                    data-testid={`media-subject-${item.id}`}
                  >
                    <input
                      type="radio"
                      name={name}
                      value={item.id}
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => onChange(item.id)}
                      className={MEDIA_SUBJECT_RADIO_CLASS}
                      // Нативная радиокнопка в цвет акцента темы — классом Tailwind это не выразить.
                      style={{ accentColor: theme.colors.AccentNaplesYellow }}
                    />
                    <span className="flex min-w-0 flex-col">
                      <UDText type="subhead" className={classNames('truncate', isDisabled ? 'text-inkDisabled' : 'text-inkPrimary')}>
                        {item.title}
                      </UDText>
                      {(item.disabledReason || item.meta) && (
                        <UDText type="footnote" className={isDisabled ? 'text-inkDisabled' : 'text-inkSecondary'}>
                          {item.disabledReason ?? item.meta}
                        </UDText>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MediaSubjectPicker;
